<template>
  <div class="page-shell course-center-page">
    <section v-if="isPhoneLayout" class="container course-mobile">
      <div v-if="categoriesLoading" class="status-card">正在加载分类数据...</div>
      <div v-else-if="categoriesError" class="status-card is-error">
        {{ categoriesError }}
      </div>
      <div v-else class="course-mobile__tabs" role="tablist" aria-label="移动端课程分类">
        <button
          v-for="tab in mobileCategoryTabs"
          :key="tab.id"
          type="button"
          :class="['course-mobile__tab', { 'is-active': selectedPrimaryCategoryId === tab.id }]"
          role="tab"
          :aria-selected="selectedPrimaryCategoryId === tab.id ? 'true' : 'false'"
          @click="selectMobileCategory(tab.id)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div v-if="coursesLoading" class="status-card">正在加载课程列表...</div>
      <div v-else-if="coursesError" class="status-card is-error">
        {{ coursesError }}
      </div>
      <div v-else-if="!mobileCourseList.length" class="status-card is-empty">
        没有更多数据了
      </div>
      <div v-else class="course-mobile__list">
        <router-link
          v-for="course in mobileCourseList"
          :key="course.id"
          class="course-mobile__item"
          :to="{ name: 'course-detail', params: { id: String(course.id) } }"
        >
          <div class="course-mobile__thumb" :style="courseCardStyle(course)">
            <div
              class="course-mobile__thumb-backdrop"
              :style="courseHeroBackdropStyle(course)"
            ></div>

            <span class="course-mobile__thumb-badge">
              {{ course.serialize_status_word || '新课' }}
            </span>

            <div class="course-mobile__thumb-copy">
              <span>{{ mobileCourseThumbLabel(course) }}</span>
            </div>
          </div>

          <div class="course-mobile__body">
            <h2>{{ course.title }}</h2>
            <p>{{ formatCourseSummary(course) }}</p>

            <div class="course-mobile__footer">
              <span>{{ mobileCourseMetaLabel(course) }}</span>
              <span>{{ mobileCourseTeacherLabel(course) }}</span>
            </div>
          </div>
        </router-link>
      </div>

      <div v-if="!coursesLoading && !coursesError && mobileCourseList.length" class="course-mobile__end">
        没有更多数据了
      </div>
    </section>

    <section v-else class="container">
      <section class="panel course-filter-hero">
        <div class="course-filter-hero__branding">
          <div class="course-filter-hero__mark" aria-hidden="true">
            <span class="course-filter-hero__play"></span>
          </div>

          <div class="course-filter-hero__copy">
            <h1>实战课</h1>
            <p>真实项目实战演练</p>
          </div>
        </div>

        <div v-if="categoriesLoading" class="status-card">正在加载分类数据...</div>
        <div v-else-if="categoriesError" class="status-card is-error">
          {{ categoriesError }}
        </div>
        <div v-else class="course-filter-hero__filters">
          <div class="course-filter-row">
            <strong>方向:</strong>

            <div class="course-filter-row__options">
              <button
                type="button"
                :class="['course-filter-chip', { 'is-active': selectedPrimaryCategoryId === 0 }]"
                @click="selectCategory(0)"
              >
                全部
              </button>

              <button
                v-for="category in primaryCategories"
                :key="category.id"
                type="button"
                :class="['course-filter-chip', { 'is-active': selectedPrimaryCategoryId === Number(category.id) }]"
                @click="selectCategory(category.id)"
              >
                {{ category.name }}
              </button>
            </div>
          </div>

          <div v-if="visibleChildCategories.length" class="course-filter-row">
            <strong>分类:</strong>

            <div class="course-filter-row__options">
              <button
                type="button"
                :class="['course-filter-chip', { 'is-active': selectedChildCategoryId === 0 }]"
                @click="selectChildCategory(0)"
              >
                不限
              </button>

              <button
                v-for="child in visibleChildCategories"
                :key="child.id"
                type="button"
                :class="['course-filter-chip', { 'is-active': selectedChildCategoryId === Number(child.id) }]"
                @click="selectChildCategory(child.id)"
              >
                {{ child.name }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section class="courses-section">
        <div class="course-results__toolbar">
          <div class="course-results__sorts" role="tablist" aria-label="课程排序">
            <button
              v-for="sort in sortOptions"
              :key="sort.id"
              type="button"
              :class="['course-results__sort', { 'is-active': activeSort === sort.id }]"
              role="tab"
              :aria-selected="activeSort === sort.id ? 'true' : 'false'"
              @click="activeSort = sort.id"
            >
              {{ sort.label }}
            </button>
          </div>

          <label class="course-results__toggle">
            <input v-model="onlyCompleted" type="checkbox">
            <span>仅看已完结</span>
          </label>

          <div class="course-results__summary">
            <span class="course-results__count">共 {{ filteredCourses.length }} 门课程</span>
          </div>
        </div>

        <div v-if="coursesLoading" class="status-card">正在加载课程列表...</div>
        <div v-else-if="coursesError" class="status-card is-error">
          {{ coursesError }}
        </div>
        <div v-else-if="!filteredCourses.length" class="status-card is-empty">
          当前筛选条件下还没有课程，试试切换其他方向或分类。
        </div>
        <div v-else class="courses-grid">
          <router-link
            v-for="course in filteredCourses"
            :key="course.id"
            class="course-card"
            :to="{ name: 'course-detail', params: { id: String(course.id) } }"
            :style="courseCardStyle(course)"
          >
            <div class="course-card__top">
              <img
                v-if="course.cover"
                class="course-card__cover"
                :src="resolveAssetUrl(course.cover)"
                :alt="course.title"
              >
              <div v-else class="course-card__placeholder">
                <span>{{ courseMonogram(course) }}</span>
              </div>

              <span class="course-card__badge">
                {{ course.serialize_status_word || '连载中' }}
              </span>
            </div>

            <div class="course-card__body">
              <h3>{{ course.title }}</h3>

              <div class="course-card__footer">
                <span
                  class="course-card__category"
                  :title="course.category_two_name || course.category_one_name || '课程专题'"
                >
                  {{ course.category_two_name || course.category_one_name || '课程专题' }}
                </span>
                <span
                  v-if="Number(course.teacher_count) > 0"
                  class="course-card__teacher-count"
                >
                  {{ course.teacher_count }} 位教师
                </span>
              </div>
            </div>
          </router-link>
        </div>
      </section>
    </section>
  </div>
</template>

<script>
import { fetchCourseCategories } from '@/api/home'
import { fetchCourseList } from '@/api/course'
import { resolveAssetUrl } from '@/utils/format'
import { getCourseMonogram, getCourseThemeStyle } from '@/utils/courseTheme'

const SORT_OPTIONS = [
  {
    id: 'latest',
    label: '最新'
  },
  {
    id: 'teachers',
    label: '讲师数'
  },
  {
    id: 'title',
    label: '课程名'
  }
]

const PHONE_MAX_WIDTH = 767
const PHONE_USER_AGENT_REGEXP = /iPhone|iPod|Android.*Mobile|Windows Phone|BlackBerry|Opera Mini|Mobile/i
const TABLET_USER_AGENT_REGEXP = /iPad|Tablet|PlayBook|Silk|Kindle|Nexus 7|Nexus 10|SM-T|Pad/i

const resolvePhoneLayout = () => {
  if (typeof window === 'undefined') {
    return false
  }

  const userAgent = window.navigator.userAgent || ''
  const isTabletDevice = TABLET_USER_AGENT_REGEXP.test(userAgent)
  const isPhoneDevice = !isTabletDevice && PHONE_USER_AGENT_REGEXP.test(userAgent)

  if (isTabletDevice) {
    return false
  }

  return isPhoneDevice || window.innerWidth <= PHONE_MAX_WIDTH
}

export default {
  name: 'CoursesView',
  data() {
    return {
      categories: [],
      categoriesLoading: true,
      categoriesError: '',
      coursesLoading: true,
      coursesError: '',
      allCourses: [],
      isPhoneLayout: resolvePhoneLayout(),
      activeSort: 'latest',
      onlyCompleted: false
    }
  },
  computed: {
    sortOptions() {
      return SORT_OPTIONS
    },
    primaryCategories() {
      return this.categories.filter(category => category && Number(category.id) > 0)
    },
    selectedCategoryId() {
      const parsed = Number(this.$route.query.category)

      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
    },
    selectedPrimaryCategoryId() {
      if (this.selectedCategoryId === 0) {
        return 0
      }

      const matchedPrimary = this.primaryCategories.find(
        category => Number(category.id) === this.selectedCategoryId
      )

      if (matchedPrimary) {
        return Number(matchedPrimary.id)
      }

      const parentCategory = this.primaryCategories.find(category =>
        Array.isArray(category.children)
        && category.children.some(child => Number(child.id) === this.selectedCategoryId)
      )

      return parentCategory ? Number(parentCategory.id) : 0
    },
    selectedChildCategoryId() {
      if (this.selectedCategoryId === 0) {
        return 0
      }

      const isPrimary = this.primaryCategories.some(
        category => Number(category.id) === this.selectedCategoryId
      )

      return isPrimary ? 0 : this.selectedCategoryId
    },
    selectedPrimaryCategory() {
      return this.primaryCategories.find(
        category => Number(category.id) === this.selectedPrimaryCategoryId
      ) || null
    },
    visibleChildCategories() {
      if (this.selectedPrimaryCategory && Array.isArray(this.selectedPrimaryCategory.children)) {
        return this.selectedPrimaryCategory.children
      }

      return this.primaryCategories.flatMap(category => category.children || [])
    },
    mobileCategoryTabs() {
      return [
        {
          id: 0,
          label: '热门'
        },
        ...this.primaryCategories.map(category => ({
          id: Number(category.id),
          label: category.name
        }))
      ]
    },
    filteredCourses() {
      let rows = this.allCourses.slice()

      if (this.selectedPrimaryCategoryId > 0) {
        rows = rows.filter(
          course => Number(course.category_one_id) === this.selectedPrimaryCategoryId
        )
      }

      if (this.selectedChildCategoryId > 0) {
        rows = rows.filter(
          course => Number(course.category_two_id) === this.selectedChildCategoryId
        )
      }

      if (this.onlyCompleted) {
        rows = rows.filter(course => Number(course.serialize_status) === 2)
      }

      return this.sortCourses(rows)
    },
    mobileCourseList() {
      return this.filteredCourses
    }
  },
  created() {
    this.loadCategories()
    this.loadCourses()
  },
  mounted() {
    this.handleViewportChange = () => {
      this.isPhoneLayout = resolvePhoneLayout()
    }

    window.addEventListener('resize', this.handleViewportChange, { passive: true })
    this.handleViewportChange()
  },
  beforeDestroy() {
    if (this.handleViewportChange) {
      window.removeEventListener('resize', this.handleViewportChange)
    }
  },
  methods: {
    resolveAssetUrl,
    courseCardStyle(course) {
      return getCourseThemeStyle(course)
    },
    courseHeroBackdropStyle(course) {
      const cover = this.resolveAssetUrl(course.cover)

      if (!cover) {
        return {}
      }

      return {
        backgroundImage: `linear-gradient(135deg, rgba(9, 16, 40, 0.08) 0%, rgba(9, 16, 40, 0.22) 100%), url(${cover})`
      }
    },
    courseMonogram(course = {}) {
      return getCourseMonogram(course)
    },
    mobileCourseThumbLabel(course = {}) {
      return course.category_two_name || course.category_one_name || '精选课程'
    },
    mobileCourseMetaLabel(course = {}) {
      const statusLabel = course.serialize_status_word || '更新中'
      const teacherCount = Number(course.teacher_count) || 0

      return `${statusLabel} · ${teacherCount}位教师`
    },
    mobileCourseTeacherLabel(course = {}) {
      return course.lecturer_name || '课程讲师'
    },
    stripHtml(value = '') {
      return String(value)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    formatCourseSummary(course = {}) {
      const plainText = this.stripHtml(course.description)

      if (!plainText) {
        return '聚焦真实业务场景，帮助你把知识点快速转成可落地的项目经验。'
      }

      return plainText.length > 54
        ? `${plainText.slice(0, 54).trim()}...`
        : plainText
    },
    parseCourseTime(course = {}) {
      const timestamp = Date.parse(course.updated_at || course.created_at || '')

      if (!Number.isNaN(timestamp)) {
        return timestamp
      }

      return Number(course.id) || 0
    },
    sortCourses(courseList = []) {
      const rows = courseList.slice()

      if (this.activeSort === 'teachers') {
        return rows.sort((first, second) => (
          (Number(second.teacher_count) || 0) - (Number(first.teacher_count) || 0)
          || this.parseCourseTime(second) - this.parseCourseTime(first)
        ))
      }

      if (this.activeSort === 'title') {
        return rows.sort((first, second) => (
          String(first.title || '').localeCompare(String(second.title || ''), 'zh-Hans-CN')
        ))
      }

      return rows.sort((first, second) => (
        this.parseCourseTime(second) - this.parseCourseTime(first)
        || (Number(second.id) || 0) - (Number(first.id) || 0)
      ))
    },
    async loadCategories() {
      this.categoriesLoading = true
      this.categoriesError = ''

      try {
        this.categories = await fetchCourseCategories()
      } catch (error) {
        this.categoriesError = error.message || '分类数据加载失败'
      } finally {
        this.categoriesLoading = false
      }
    },
    async loadCourses() {
      this.coursesLoading = true
      this.coursesError = ''

      try {
        const response = await fetchCourseList({
          page: 0
        })

        this.allCourses = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        this.coursesError = error.message || '课程列表加载失败'
        this.allCourses = []
      } finally {
        this.coursesLoading = false
      }
    },
    selectCategory(categoryId) {
      const normalizedId = Number(categoryId) || 0

      this.$router.push({
        name: 'courses',
        query: {
          ...(normalizedId > 0 ? { category: String(normalizedId) } : {})
        }
      }).catch(() => {})
    },
    selectMobileCategory(categoryId) {
      this.selectCategory(categoryId)
    },
    selectChildCategory(categoryId) {
      const normalizedId = Number(categoryId) || 0

      if (normalizedId > 0) {
        this.selectCategory(normalizedId)
        return
      }

      this.selectCategory(this.selectedPrimaryCategoryId)
    }
  }
}
</script>

<style scoped lang="scss">
.course-center-page {
  display: grid;
  gap: 28px;
}

.course-mobile {
  display: grid;
  gap: 16px;
  min-height: calc(100vh - var(--header-height) - 100px);
}

.course-mobile__tabs {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 2px;
  scrollbar-width: none;
}

.course-mobile__tabs::-webkit-scrollbar {
  display: none;
}

.course-mobile__tab {
  flex: 0 0 auto;
  min-height: 38px;
  padding: 0 16px;
  border: 1px solid rgba($color-border, 0.98);
  border-radius: 999px;
  background: rgba($color-surface, 0.98);
  color: #4b5563;
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.course-mobile__tab:hover {
  transform: translateY(-1px);
}

.course-mobile__tab.is-active {
  border-color: transparent;
  background: linear-gradient(135deg, #ff2d14 0%, #ff4a39 100%);
  box-shadow: 0 10px 22px rgba(255, 45, 20, 0.18);
  color: $color-surface;
}

.course-mobile__list {
  display: grid;
  gap: 18px;
  min-height: 400px;
}

.course-mobile__item {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 14px;
  align-items: start;
}

.course-mobile__thumb {
  position: relative;
  overflow: hidden;
  min-height: 88px;
  border-radius: 18px;
  background: linear-gradient(135deg, var(--course-theme-primary) 0%, var(--course-theme-secondary) 100%);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.12);
}

.course-mobile__thumb::after {
  content: '';
  position: absolute;
  right: -18px;
  bottom: -20px;
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.1);
}

.course-mobile__thumb-backdrop {
  position: absolute;
  inset: 0;
  background-position: center;
  background-size: cover;
  mix-blend-mode: soft-light;
  opacity: 0.62;
}

.course-mobile__thumb-badge {
  position: absolute;
  top: 6px;
  right: 8px;
  z-index: 1;
  color: #fff2aa;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.04em;
}

.course-mobile__thumb-copy {
  position: relative;
  z-index: 1;
  display: grid;
  align-content: center;
  min-height: 88px;
  padding: 12px 10px;
  color: $color-surface;
  text-align: center;
}

.course-mobile__thumb-copy span {
  font-size: 13px;
  line-height: 1.4;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.course-mobile__body {
  display: grid;
  gap: 8px;
  min-width: 0;
}

.course-mobile__body h2 {
  color: #1f2937;
  font-size: 16px;
  line-height: 1.5;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.course-mobile__body p {
  color: #667085;
  font-size: 14px;
  line-height: 1.6;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.course-mobile__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  color: #8d97a5;
  font-size: 12px;
}

.course-mobile__end {
  padding: 8px 0 4px;
  color: #9aa3af;
  font-size: 14px;
  text-align: center;
}

.course-filter-hero {
  position: relative;
  overflow: hidden;
  display: grid;
  gap: 28px;
  padding: 28px 32px 30px;
  border: 1px solid rgba($color-border, 0.84);
  border-radius: 32px;
  background:
    radial-gradient(circle at top right, rgba($color-primary, 0.08), transparent 28%),
    radial-gradient(circle at left center, rgba($color-accent, 0.08), transparent 24%),
    linear-gradient(180deg, rgba($color-surface, 0.98) 0%, rgba(#fff7f0, 0.98) 100%);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
}

.course-filter-hero__branding {
  display: flex;
  align-items: center;
  gap: 16px;
}

.course-filter-hero__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f3a300 0%, #ffbc3c 100%);
  box-shadow: 0 14px 30px rgba(243, 163, 0, 0.22);
}

.course-filter-hero__play {
  width: 0;
  height: 0;
  margin-left: 4px;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-left: 13px solid $color-surface;
}

.course-filter-hero__copy {
  display: grid;
  gap: 4px;
}

.course-filter-hero__copy h1 {
  font-size: clamp(30px, 4vw, 38px);
  line-height: 1;
}

.course-filter-hero__copy p {
  color: $color-text-secondary;
  font-size: 15px;
}

.course-filter-hero__filters {
  display: grid;
  gap: 24px;
}

.course-filter-row {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  align-items: start;
  gap: 14px 20px;
}

.course-filter-row strong {
  padding-top: 8px;
  color: #111827;
  font-size: 15px;
  font-weight: 900;
}

.course-filter-row__options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 12px;
}

.course-filter-chip {
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 0 14px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #263142;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.course-filter-chip:hover {
  background: rgba($color-primary-light, 0.72);
  color: $color-primary;
}

.course-filter-chip.is-active {
  background: rgba($color-primary-light, 0.98);
  color: $color-primary;
  box-shadow: inset 0 0 0 1px rgba($color-primary, 0.08);
}

.courses-section {
  margin-top: 20px;
  display: grid;
  gap: 22px;
}

.course-results__toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
}

.course-results__sorts {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.course-results__sort {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  color: #475569;
  font-size: 14px;
  font-weight: 700;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.course-results__sort:hover,
.course-results__sort.is-active {
  border-color: rgba($color-primary, 0.14);
  background: rgba($color-surface, 0.94);
  color: #111827;
}

.course-results__toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-left: 8px;
  color: $color-primary;
  font-size: 14px;
  font-weight: 700;
}

.course-results__toggle input {
  width: 16px;
  height: 16px;
  margin: 0;
  padding: 0;
  accent-color: $color-primary;
}

.course-results__summary {
  margin-left: auto;
}

.course-results__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 0 16px;
  border-radius: 999px;
  background: rgba($color-primary-light, 0.9);
  color: $color-primary;
  font-size: 14px;
  font-weight: 800;
}

.courses-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 20px;
}

.course-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  border-radius: 18px;
  background: rgba($color-surface, 0.98);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.course-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.12);
}

.course-card__top {
  position: relative;
  height: 184px;
  overflow: hidden;
  background: linear-gradient(135deg, var(--course-theme-primary) 0%, var(--course-theme-secondary) 100%);
}

.course-card__cover,
.course-card__placeholder {
  display: block;
  width: 100%;
  height: 100%;
}

.course-card__cover {
  object-fit: cover;
}

.course-card__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.84);
  font-size: 34px;
  font-weight: 900;
}

.course-card__badge {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  color: $color-surface;
  font-size: 12px;
  font-weight: 800;
  backdrop-filter: blur(8px);
}

.course-card__body {
  display: flex;
  min-height: 146px;
  flex: 1;
  flex-direction: column;
  padding: 18px 16px 16px;
}

.course-card__body h3 {
  display: -webkit-box;
  min-height: 54px;
  margin: 0;
  overflow: hidden;
  color: #1f2937;
  font-size: 18px;
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.course-card__footer {
  display: flex;
  min-width: 0;
  margin-top: auto;
  padding-top: 16px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px solid rgba($color-border, 0.72);
  color: #8b94a6;
  font-size: 13px;
}

.course-card__category {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  color: #606a7b;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.course-card__teacher-count {
  flex: 0 0 auto;
  white-space: nowrap;
}

@media (max-width: 1180px) {
  .courses-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .course-filter-hero {
    padding: 24px;
  }

  .course-filter-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .course-filter-row strong {
    padding-top: 0;
  }

  .courses-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

}

@media (max-width: 760px) {
  .course-results__summary {
    margin-left: 0;
  }

  .courses-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .course-filter-hero {
    gap: 22px;
    padding: 20px;
    border-radius: 24px;
  }

  .course-filter-hero__branding {
    align-items: flex-start;
  }

  .course-filter-hero__copy h1 {
    font-size: 28px;
  }

  .course-filter-chip {
    min-height: 36px;
    padding: 0 12px;
    font-size: 14px;
  }

  .course-results__toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .course-results__toggle {
    margin-left: 0;
  }

  .courses-grid {
    grid-template-columns: 1fr;
  }

  .course-card__top {
    height: 172px;
  }
}
</style>
